import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [wishlistIds, setWishlistIds] = useState([]);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!user) {
      setWishlistIds([]);
      return;
    }
    api.get("/me/wishlist")
      .then((response) => setWishlistIds(response.data.map((product) => product.id)))
      .catch(() => setWishlistIds([]));
  }, [user]);

  const toggleWishlist = async (productId) => {
    const { data } = await api.post(`/me/wishlist/${productId}`);
    setWishlistIds(data.wishlist || []);
    return data.action;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password, consentAccepted) => {
    const { data } = await api.post("/auth/register", {
      name,
      email,
      password,
      consent_accepted: consentAccepted,
    });
    setUser(data.user);
    return data.user;
  };

  // Used by OTP flow — the server has already set the httpOnly session cookie.
  const setSession = (data) => {
    if (!data?.user) return;
    setUser(data.user);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Clear local state even if the server session already expired.
    } finally {
      setUser(null);
      setWishlistIds([]);
    }
  };

  const updateCurrentUser = (nextUser) => {
    setUser((current) => ({ ...current, ...nextUser }));
  };

  return (
    <AuthCtx.Provider value={{ user, checking, wishlistIds, toggleWishlist, login, register, setSession, logout, updateCurrentUser }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
